import { apiJson, apiRaw } from '@/lib/http';

// Use shared helper
const apiCall = <T = unknown>(endpoint: string, options: RequestInit = {}) => apiJson<T>(endpoint, options);

// Helper function for file downloads (uses shared raw helper)
const downloadFile = async (endpoint: string, filename: string) => {
  const response = await apiRaw(endpoint, { method: 'GET', headers: { 'Accept': 'text/csv' } });
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// Backend API response types
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface AuditLogDto {
  id: string;
  eventType: string;
  userId?: string;
  projectId?: string;
  timestamp: string;
  success: boolean;
  metadata: string;
  ipAddress: string;
  userAgent: string;
}

export interface AuditLogRequest {
  userId?: string;
  projectId?: string;
  startDate?: string;
  endDate?: string;
  eventType?: string;
  page?: number;
  pageSize?: number;
}

export interface AuditLogsResponse {
  logs: AuditLogDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const auditService = {
  // Get audit logs with pagination and filtering
  async getAuditLogs(request: AuditLogRequest = {}): Promise<AuditLogsResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (request.userId) queryParams.append('UserId', request.userId);
      if (request.projectId) queryParams.append('ProjectId', request.projectId);
      if (request.startDate) queryParams.append('StartDate', request.startDate);
      if (request.endDate) queryParams.append('EndDate', request.endDate);
      if (request.eventType && request.eventType !== 'all') queryParams.append('EventType', request.eventType);
      if (request.page) queryParams.append('Page', request.page.toString());
      if (request.pageSize) queryParams.append('PageSize', request.pageSize.toString());
      
      const endpoint = `/audit/logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response: AuditLogsResponse = await apiCall(endpoint);
      return response;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      throw error;
    }
  },

  // Export audit logs as CSV
  async exportAuditLogs(request: AuditLogRequest = {}): Promise<void> {
    try {
      const queryParams = new URLSearchParams();
      if (request.userId) queryParams.append('UserId', request.userId);
      if (request.projectId) queryParams.append('ProjectId', request.projectId);
      if (request.startDate) queryParams.append('StartDate', request.startDate);
      if (request.endDate) queryParams.append('EndDate', request.endDate);
      if (request.eventType && request.eventType !== 'all') queryParams.append('EventType', request.eventType);
      
      const endpoint = `/audit/export${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const filename = `audit_logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
      
      await downloadFile(endpoint, filename);
    } catch (error) {
      console.error('Error exporting audit logs:', error);
      throw error;
    }
  }
}; 